import { fetchAndActivate, getRemoteConfig, getValue } from "firebase/remote-config";
import { app } from "../notification/firebase";

export const remoteConfig = getRemoteConfig(app);

const defaultConfigValues = {
  test_backend: "https://testapp.memotrack.net",
  test_frontend: "https://testweb.memotrack.net",
  backend: "https://app.memotrack.net",
  frontend: "https://memotrack.net",
  build_number: "20",
  version: "1.0.0"
};

// Optional: Set default values for when we are offline or before config fetches
remoteConfig.defaultConfig = defaultConfigValues;

let resolvedConfig = {
  backendUrl: defaultConfigValues.test_backend,
  frontendUrl: defaultConfigValues.test_frontend,
  buildNumber: defaultConfigValues.build_number,
  version: defaultConfigValues.version
};

const configListeners = new Set();

const updateResolvedConfig = (nextConfig = {}) => {
  resolvedConfig = {
    backendUrl: nextConfig.backendUrl || defaultConfigValues.test_backend,
    frontendUrl: nextConfig.frontendUrl || defaultConfigValues.test_frontend,
    buildNumber: nextConfig.buildNumber || defaultConfigValues.build_number,
    version: nextConfig.version || defaultConfigValues.version
  };

  configListeners.forEach((listener) => listener(resolvedConfig));
};

export const subscribeToRemoteConfig = (listener) => {
  configListeners.add(listener);
  return () => configListeners.delete(listener);
};

/**
 * Helper to determine if we are in a testing environment.
 * - Localhost is always testing.
 * - NODE_ENV development is testing.
 * Adjust as needed (e.g., check for a specific test domain).
 */
export const isTestingEnvironment = () => {
  const hostname = window.location.hostname;
  // It is a testing environment if the hostname is NOT exactly web.memotrack.net
  return hostname !== 'web.memotrack.net';
};

// Initialize and fetch the remote config
export const initRemoteConfig = async () => {
  try {
    // In development mode, you might want to lower the fetch interval to get updates faster.
    if (process.env.NODE_ENV === "development") {
      remoteConfig.settings.minimumFetchIntervalMillis = 36000; // 1 hour for dev, or lower
    }

    await fetchAndActivate(remoteConfig);

    const isTest = isTestingEnvironment();
    const nextConfig = {
      backendUrl: isTest
        ? getValue(remoteConfig, "test_backend").asString()
        : getValue(remoteConfig, "backend").asString(),
      frontendUrl: isTest
        ? getValue(remoteConfig, "test_frontend").asString()
        : getValue(remoteConfig, "frontend").asString(),
      buildNumber: getValue(remoteConfig, "build_number").asString(),
      version: getValue(remoteConfig, "version").asString()
    };

    updateResolvedConfig(nextConfig);
    console.log("Remote config fetched and activated", nextConfig);
  } catch (error) {
    console.error("Failed to fetch remote config:", error);
    updateResolvedConfig();
  }
};

/**
 * Retrieve the correct configuration based on the environment.
 */
export const AppConfig = {
  get backendUrl() {
    return resolvedConfig.backendUrl;
  },
  get frontendUrl() {
    return resolvedConfig.frontendUrl;
  },
  get buildNumber() {
    return resolvedConfig.buildNumber;
  },
  get version() {
    return resolvedConfig.version;
  }
};
